import { useEffect, useState } from 'react';
import { customerApi } from '@/lib/api';
import { BottomNav } from '@/components/customer/BottomNav';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RedemptionCalendar } from '@/components/shared/RedemptionCalendar';

const TZ = 'America/Edmonton';

function groupByMonth(redemptions) {
  const groups = {};
  for (const r of redemptions) {
    const key = new Date(r.createdAt).toLocaleDateString('en-CA', { timeZone: TZ, month: 'long', year: 'numeric' });
    (groups[key] = groups[key] || []).push(r);
  }
  return groups;
}

export default function History() {
  const [redemptions, setRedemptions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    customerApi.get('/redemptions/me').then(({ data }) => setRedemptions(data.redemptions));
    customerApi.get('/subscriptions/me').then(({ data }) => setSubscriptions(data.subscriptions));
  }, []);

  const monthGroups = groupByMonth(redemptions);

  return (
    <div className="min-h-screen bg-bone pb-24">
      <div className="max-w-md mx-auto px-6 pt-8">
        <h1 className="font-heading text-2xl font-700 text-charcoal mb-4">Tiffin History</h1>

        <Tabs defaultValue="calendar">
          <TabsList className="grid w-full grid-cols-3" data-testid="history-tabs">
            <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-4">
            <RedemptionCalendar redemptions={redemptions} />
          </TabsContent>

          <TabsContent value="list" className="space-y-4 mt-4">
            {redemptions.length === 0 && <p className="text-sm text-muted-ink text-center py-8">No pickups yet.</p>}
            {Object.entries(monthGroups).map(([month, rows]) => (
              <div key={month}>
                <p className="text-xs font-semibold text-muted-ink uppercase tracking-wide mb-2">{month}</p>
                <div className="space-y-2">
                  {rows.map((r) => (
                    <div key={r.id} data-testid="redemption-row" className="bg-white rounded-xl border border-black/5 p-4 flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-charcoal">
                          {new Date(r.createdAt).toLocaleDateString('en-CA', { timeZone: TZ })}{' '}
                          <span className="text-muted-ink">
                            {new Date(r.createdAt).toLocaleTimeString('en-CA', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </p>
                        <p className="text-xs text-muted-ink mt-0.5">
                          {r.balanceBefore} → {r.balanceAfter} remaining
                        </p>
                      </div>
                      <span className="font-heading text-lg font-700 text-saffron">-{r.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-2 mt-4">
            {subscriptions.length === 0 && <p className="text-sm text-muted-ink text-center py-8">No subscriptions yet.</p>}
            {subscriptions.map((s) => (
              <div key={s.id} data-testid="subscription-history-row" className="bg-white rounded-xl border border-black/5 p-4">
                <div className="flex justify-between items-start">
                  <p className="font-medium text-charcoal">{s.plan?.name}</p>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-bone text-muted-ink">{s.status}</span>
                </div>
                <p className="text-xs text-muted-ink mt-1">
                  {new Date(s.startDate).toLocaleDateString('en-CA')} – {new Date(s.expiryDate).toLocaleDateString('en-CA')} ·{' '}
                  {s.remainingCredits}/{s.originalCredits} remaining
                </p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
      <BottomNav />
    </div>
  );
}
